import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
} from '@mui/joy';
import { useMobileMode } from 'components/Responsive';
import Meta from 'components/Meta';
import React, { useEffect, useState } from 'react';
import {
  FiArrowLeft,
  FiExternalLink,
} from 'react-icons/fi';
import { RiBracesLine } from 'react-icons/ri';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Project } from 'assets/Projects';
import details from 'assets/Details';
import getProjects from './staticProjectsData';
// import { beautify } from '../sections/Featured';
import ProjectSectionRenderer from './ProjectSectionRenderer';

export default function ProjectDetail() {
  const { title } = useParams<{ title: string }>();
  const navigate = useNavigate();
  const mobile = useMobileMode();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      const projects = await getProjects();
      const foundProject = projects.find(
        (p) => p.title.toLowerCase().replace(/\s+/g, '-') === title?.toLowerCase(),
      );

      if (foundProject) {
        setProject(foundProject);
      } else {
        navigate('/projects');
      }
      setLoading(false);
    };
    loadProject();
  }, [title, navigate]);

  if (loading) {
    return null;
  }

  if (!project) {
    return null;
  }

  return (
    <Stack
      width="100vw"
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      sx={{
        paddingLeft: 'var(--nav-safe-area-inset-left)',
        paddingBottom: 'var(--nav-safe-area-inset-bottom)',
      }}
    >
      <Meta title={`${project.title} - ${details.name.first} ${details.name.last}`} />
      <Stack
        padding={mobile ? '2rem 1rem' : '5rem'}
        gap={mobile ? 3 : 4}
        alignItems={mobile ? 'center' : 'start'}
        maxWidth="1200px"
        width="100%"
        margin="0 auto"
      >
        <Button
          size="lg"
          component={Link}
          to="/projects"
          variant="outlined"
          color="neutral"
          startDecorator={<FiArrowLeft />}
          sx={(theme) => ({
            transition: 'all ease .2s',
            borderRadius: '0',
            background: theme.palette.background.body,
            alignSelf: mobile ? 'flex-start' : 'auto',
            '&:hover': {
              background: theme.palette.text.primary,
              color: theme.palette.background.level1,
              borderColor: theme.palette.text.primary,
            },
          })}
        >
          Back to Projects
        </Button>

        <Typography
          level={mobile ? 'h2' : 'h1'}
          fontWeight="xl"
          sx={{
            textAlign: mobile ? 'center' : 'left',
            width: '100%',
          }}
        >
          {project.title}
        </Typography>

        {project.thumbnail && (
          <Card
            variant="outlined"
            sx={{
              width: '100%',
              overflow: 'hidden',
              padding: 0,
              position: 'relative',
            }}
          >
            <Box
              component="img"
              src={project.thumbnail}
              alt={`${project.title} - Thumbnail`}
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </Card>
        )}

        {/* Project content sections */}
        <Stack
          gap={mobile ? 3 : 6}
          width="100%"
          paddingLeft={mobile ? 0 : '70px'}
          marginBottom={mobile ? '5rem' : 0}
        >
          {/* Project Overview Section */}
          <Stack
            gap={mobile ? 2 : 1}
            alignItems={mobile ? 'center' : 'flex-start'}
            sx={(theme) => ({
              width: '100%',
              position: 'relative',
              '&::before': mobile ? {} : {
                content: '""',
                position: 'absolute',
                width: '2px',
                height: 'calc(100% + 50px)',
                background: `linear-gradient(to bottom, ${theme.palette.primary[500]}, ${theme.palette.info[500]})`,
                left: '-49px',
                top: '0',
              },
            })}
          >
            {mobile && (
              <Avatar
                alt="Project Overview"
                color="primary"
                sx={(theme) => ({
                  position: 'relative',
                  border: 'none',
                  outline: `2px solid ${theme.palette.primary[500]}`,
                  boxShadow: `0 0 40px 5px rgba(${theme.palette.primary.mainChannel} / 0.4)`,
                  overflow: 'visible',
                  marginTop: '3rem',
                  marginBottom: '1rem',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-5rem',
                    height: '5rem',
                    width: '2px',
                    background: `linear-gradient(to bottom, transparent, ${theme.palette.primary[500]})`,
                  },
                })}
              >
                <HiOutlineDocumentText />
              </Avatar>
            )}
            <Stack gap={1} width="100%">
              <Typography
                level="h2"
                fontWeight="lg"
                sx={{
                  position: 'relative',
                  textAlign: mobile ? 'center' : 'left',
                }}
              >
                {!mobile && (
                  <Avatar
                    alt="Project Overview"
                    color="primary"
                    sx={(theme) => ({
                      position: 'absolute',
                      left: '-49px',
                      top: '0',
                      transform: 'translateX(-50%)',
                      border: 'none',
                      outline: `2px solid ${theme.palette.primary[500]}`,
                      boxShadow: `0 0 40px 5px rgba(${theme.palette.primary.mainChannel} / 0.4)`,
                    })}
                  >
                    <HiOutlineDocumentText />
                  </Avatar>
                )}
                Project Overview
              </Typography>
              <Typography
                level="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.7,
                  textAlign: mobile ? 'center' : 'left',
                }}
              >
                {project.description}
              </Typography>
              {project.skills && project.skills.length > 0 && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  paddingY={0.5}
                  justifyContent={mobile ? 'center' : 'flex-start'}
                >
                  {project.skills.map((skill) => (
                    <Chip key={skill} size="sm" variant="outlined" color="neutral">
                      {skill}
                    </Chip>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>

          {/* Dynamic Project Sections */}
          {project.sections && project.sections.length > 0 && (
            project.sections.map((section, idx) => (
              <ProjectSectionRenderer
                key={`section-${section.title}`}
                section={section}
                index={idx}
                isLast={idx === project.sections!.length - 1}
              />
            ))
          )}
        </Stack>

        <Stack direction={mobile ? 'column' : 'row'} gap={2} width="100%">
          {project.source && (
            <Button
              component="a"
              href={project.source}
              target="_blank"
              size="lg"
              color="neutral"
              variant="outlined"
              sx={(theme) => ({
                transition: 'all ease .2s',
                borderRadius: '0',
                flex: 1,
                background: theme.palette.background.body,
                '&:hover': {
                  background: theme.palette.text.primary,
                  color: theme.palette.background.level1,
                  borderColor: theme.palette.text.primary,
                },
              })}
              startDecorator={<RiBracesLine />}
            >
              View Source Code
            </Button>
          )}
          {project.demo && (
            <Button
              component="a"
              href={project.demo}
              target="_blank"
              size="lg"
              color="neutral"
              variant="solid"
              sx={(theme) => ({
                transition: 'all ease .2s',
                borderRadius: '0',
                flex: 1,
                borderColor: theme.palette.text.primary,
                background: theme.palette.text.primary,
                color: theme.palette.background.level1,
                '&:hover': {
                  backgroundColor: theme.palette.background.body,
                  color: theme.palette.text.primary,
                },
              })}
              startDecorator={<FiExternalLink />}
            >
              Visit Website
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
